import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getCrmGuests, crmGuestsExportUrl, getCrmTasks, createCrmTask,
  updateCrmTask, deleteCrmTask, getAvailableUsers,
  bulkTagCrmGuests, bulkCreateCrmTasks, createCrmSegment, deleteCrmSegment, deleteCrmTag,
} from '../services/api';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PlanUpsellWall from '../components/common/PlanUpsellWall';
import CrmGuestDrawer from '../components/crm/CrmGuestDrawer';
import AnalyticsTab from '../components/crm/AnalyticsTab';
import PromotionsTab from '../components/crm/PromotionsTab';
import OffersTab from '../components/crm/OffersTab';
import MailingsTab from '../components/crm/MailingsTab';
import CertificatesTab from '../components/crm/CertificatesTab';
import TasksKanban from '../components/crm/TasksKanban';
import TaskDrawer from '../components/crm/TaskDrawer';
import { TASK_STAGE_LABELS, TASK_CLOSED_REASON_LABELS } from '../components/crm/taskStages';
import { RFM_TITLES } from '../components/crm/rfmSegments';
import GuestLookup from '../components/loyalty/GuestLookup';
import { formatMoney, formatNumber, formatDate } from '../utils/formatters';
import './CrmPage.css';

/* Иконки вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconWarning = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;
const IconTag = () => <Icon><path d="M4.72848 16.1369C3.18295 14.5914 2.41018 13.8186 2.12264 12.816C1.83509 11.8134 2.08083 10.7485 2.57231 8.61875L2.85574 7.39057C3.26922 5.59881 3.47597 4.70292 4.08944 4.08944C4.70292 3.47597 5.59881 3.26922 7.39057 2.85574L8.61875 2.57231C10.7485 2.08083 11.8134 1.83509 12.816 2.12264C13.8186 2.41018 14.5914 3.18295 16.1369 4.72848L17.9665 6.55812C20.6555 9.24711 22 10.5916 22 12.2623C22 13.933 20.6555 15.2775 17.9665 17.9665C15.2775 20.6555 13.933 22 12.2623 22C10.5916 22 9.24711 20.6555 6.55812 17.9665L4.72848 16.1369Z" /><circle cx="8.607" cy="8.879" r="2" transform="rotate(-45 8.607 8.879)" /><path d="M11.5417 18.5L18.5208 11.5208" /></Icon>;
const IconSave = () => <Icon><path d="M3.46447 20.5355C4.92893 22 7.28595 22 12 22C16.714 22 19.0711 22 20.5355 20.5355C22 19.0711 22 16.714 22 12C22 11.6585 22 11.4878 21.9848 11.3142C21.9142 10.5049 21.586 9.71257 21.0637 9.09034C20.9516 8.95687 20.828 8.83317 20.5806 8.58578L15.4142 3.41944C15.1668 3.17206 15.0431 3.04835 14.9097 2.93631C14.2874 2.414 13.4951 2.08581 12.6858 2.01515C12.5122 2 12.3415 2 12 2C7.28595 2 4.92893 2 3.46447 3.46447C2 4.92893 2 7.28595 2 12C2 16.714 2 19.0711 3.46447 20.5355Z" /><path d="M17 22V21C17 19.1144 17 18.1716 16.4142 17.5858C15.8284 17 14.8856 17 13 17H11C9.11438 17 8.17157 17 7.58579 17.5858C7 18.1716 7 19.1144 7 21V22" /><path d="M7 8H13" /></Icon>;
const IconClose = () => <Icon><path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" /></Icon>;
const IconDownload = () => <Icon><path d="M17 9.00195C19.175 9.01406 20.3529 9.11051 21.1213 9.8789C22 10.7576 22 12.1718 22 15.0002V16.0002C22 18.8286 22 20.2429 21.1213 21.1215C20.2426 22.0002 18.8284 22.0002 16 22.0002H8C5.17157 22.0002 3.75736 22.0002 2.87868 21.1215C2 20.2429 2 18.8286 2 16.0002L2 15.0002C2 12.1718 2 10.7576 2.87868 9.87889C3.64706 9.11051 4.82497 9.01406 7 9.00195" /><path d="M12 2L12 15M15 11.5L12 15L9 11.5" /></Icon>;
const IconNote = () => <Icon><path d="M2 4.99792V16.1437C2 17.2547 2.90605 18.1536 4.01486 18.2229C4.98428 18.2836 6.12329 18.4021 7 18.6335C8.08145 18.9189 9.37293 19.5366 10.3724 20.0694C11.3852 20.6092 12.6148 20.6092 13.6276 20.0694C14.6271 19.5366 15.9185 18.9189 17 18.6335C17.8767 18.4021 19.0157 18.2836 19.9851 18.2229C21.094 18.1536 22 17.2547 22 16.1437V4.93332C22 3.86087 21.1538 2.98053 20.082 3.01787C18.9534 3.05718 17.5469 3.17415 16.5 3.48757C15.5924 3.75929 14.5353 4.30431 13.6738 4.80287C12.6355 5.40379 11.3428 5.43636 10.2823 4.87558C9.29611 4.35413 8.04921 3.76443 7 3.48757C6.11349 3.25363 4.95877 3.13502 3.9824 3.07501C2.8863 3.00764 2 3.89975 2 4.99792Z" /><path d="M12 5.50049V20.5005" /></Icon>;
const IconCheckCircle = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M8.5 12.5L10.5 14.5L15.5 9.5" /></Icon>;
const IconArrowLeft = () => <Icon><path d="M20 12H4M10 18L4 12L10 6" /></Icon>;
const IconArrowRight = () => <Icon><path d="M4 12H20M14 18L20 12L14 6" /></Icon>;
const IconUsersGroup = () => <Icon><circle cx="9" cy="6" r="4" /><path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" /><ellipse cx="9" cy="17" rx="7" ry="4" /><path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" /></Icon>;

const STATUS_META = {
  active:  { title: 'Активные', cls: 'st-active', hint: 'приходят в своём обычном ритме' },
  risk:    { title: 'Под риском', cls: 'st-risk', hint: 'пауза длиннее обычной' },
  lost:    { title: 'Потерянные', cls: 'st-lost', hint: 'давно не было визитов' },
  unknown: { title: 'Нет данных', cls: 'st-unknown', hint: 'ещё не сверялись с iiko' },
};

const PAGE = 100;


const BulkBar = ({ count, availableUsers, onTag, onCreateTask, onClear }) => {
  const [tag, setTag] = useState('');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);

  const addTag = async () => {
    if (!tag.trim()) return;
    setBusy(true);
    try { await onTag(tag.trim()); setTag(''); } finally { setBusy(false); }
  };

  const addTask = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onCreateTask({ title: title.trim(), due_date: due || null, assignee_id: assignee || null });
      setTitle(''); setDue(''); setAssignee('');
    } finally { setBusy(false); }
  };

  return (
    <div className="crm-bulk-bar">
      <strong>Выбрано: {count}</strong>
      <input type="text" placeholder="Тег" value={tag} onChange={(e) => setTag(e.target.value)} />
      <button className="ui-btn-ghost" disabled={busy || !tag.trim()} onClick={addTag}>Добавить тег</button>
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--c-border)' }} />
      <input type="text" placeholder="Задача" value={title} onChange={(e) => setTitle(e.target.value)} />
      <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
        <option value="">Исполнитель</option>
        {availableUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <button className="ui-btn-ghost" disabled={busy || !title.trim()} onClick={addTask}>
        Создать задачу всем
      </button>
      <button className="ui-btn-ghost" onClick={onClear} style={{ marginLeft: 'auto' }}>
        Отменить выбор
      </button>
    </div>
  );
};

const GuestsTab = ({ availableUsers, preset }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // preset — фильтр, с которым сюда перешли из аналитики (статус или RFM)
  const [status, setStatus] = useState(preset?.status || '');
  const [rfm, setRfm] = useState(preset?.rfm || '');
  const [tag, setTag] = useState('');
  const [excluded, setExcluded] = useState('');   // 'only' — только снятые с учёта
  const [search, setSearch] = useState(preset?.search || '');
  // В поле — то, что набирается; в запрос уходит через 300 мс тишины. Раньше
  // каждая буква пересчитывала список из 10 тысяч гостей на сервере.
  const [searchInput, setSearchInput] = useState(preset?.search || '');
  const [page, setPage] = useState(0);
  const requestSeq = useRef(0);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [segmentName, setSegmentName] = useState('');

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setError('');
      const res = await getCrmGuests({
        status, tag, search, rfm, excluded, limit: PAGE, offset: page * PAGE,
      });
      if (seq === requestSeq.current) setData(res);   // ответ на устаревший запрос не показываем
    } catch (err) {
      if (seq === requestSeq.current) setError(err.response?.data?.error || 'Не удалось загрузить гостей');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [status, tag, search, rfm, excluded, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => { setSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search]);
  useEffect(() => { setChecked(new Set()); }, [status, tag, search, rfm, excluded, page]);

  const pick = (setter, value) => { setter(value); setPage(0); };

  const applySegment = (seg) => {
    pick(setStatus, seg.filters.status || '');
    setTag(seg.filters.tag || '');
    setSearch(seg.filters.search || '');
    setSearchInput(seg.filters.search || '');
    setPage(0);
  };

  const saveSegment = async () => {
    if (!segmentName.trim()) return;
    await createCrmSegment(segmentName.trim(), { status, tag, search });
    setSegmentName('');
    load();
  };

  const removeSegment = async (id, e) => {
    e.stopPropagation();
    await deleteCrmSegment(id);
    load();
  };

  const removeTag = async (t, e) => {
    e.stopPropagation();
    if (!window.confirm(`Удалить тег «${t.tag}» у всех гостей (${t.count})?`)) return;
    await deleteCrmTag(t.tag);
    if (tag === t.tag) pick(setTag, '');
    else load();
  };

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setChecked((prev) => {
      if (prev.size === (data?.guests || []).length && prev.size > 0) return new Set();
      return new Set((data?.guests || []).map((g) => g.crm.id));
    });
  };

  const bulkTag = async (t) => {
    await bulkTagCrmGuests(Array.from(checked), t);
    setChecked(new Set());
    load();
  };

  const bulkTask = async (payload) => {
    await bulkCreateCrmTasks(Array.from(checked), payload);
    setChecked(new Set());
    load();
  };

  const totalFiltered = data?.total_filtered || 0;
  const pages = Math.ceil(totalFiltered / PAGE);
  const summary = data?.summary;

  return (
    <>
      {loading && <LoadingSpinner />}
      {!loading && error && (
        <div className="loyalty-error"><span><IconWarning /></span><div><strong>{error}</strong></div></div>
      )}

      {!loading && !error && data && (
        <>
          <GuestLookup />

          {summary && (
            <div className="gb-cards">
              {['active', 'risk', 'lost'].map((key) => {
                const meta = STATUS_META[key];
                return (
                  <button
                    key={key}
                    className={`gb-card ${meta.cls} ${status === key ? 'selected' : ''}`}
                    onClick={() => pick(setStatus, status === key ? '' : key)}
                  >
                    <span className="gb-card-title">{meta.title}</span>
                    <span className="gb-card-num">{formatNumber(summary[key] || 0)}</span>
                    <span className="gb-card-hint">{meta.hint}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="crm-filters panel">
            <div className="gb-chips">
              {rfm && (
                <div className="gb-chip gb-chip-segment active">
                  <span className="gb-chip-apply">RFM: {RFM_TITLES[rfm] || rfm}</span>
                  <button
                    type="button"
                    className="gb-chip-n"
                    onClick={() => pick(setRfm, '')}
                    aria-label="Снять фильтр RFM"
                  >
                    <IconClose />
                  </button>
                </div>
              )}
              {['active', 'risk', 'lost'].map((key) => (
                <button
                  key={key}
                  className={`gb-chip ${status === key ? 'active' : ''}`}
                  onClick={() => pick(setStatus, status === key ? '' : key)}
                >
                  {STATUS_META[key].title}
                </button>
              ))}
              {(data.excluded_count > 0 || excluded) && (
                <button
                  className={`gb-chip ${excluded ? 'active' : ''}`}
                  onClick={() => pick(setExcluded, excluded ? '' : 'only')}
                  title="Гости, снятые с учёта: их нет в аналитике, акциях и авто-задачах"
                >
                  Не учитываются <span className="gb-chip-n">{data.excluded_count || 0}</span>
                </button>
              )}
              {(data.tags || []).map((t) => (
                <div key={t.tag} className={`gb-chip gb-chip-segment ${tag === t.tag ? 'active' : ''}`}>
                  <button
                    type="button"
                    className="gb-chip-apply"
                    onClick={() => pick(setTag, tag === t.tag ? '' : t.tag)}
                  >
                    <IconTag /> {t.tag} <span className="gb-chip-count">{t.count}</span>
                  </button>
                  <button
                    type="button"
                    className="gb-chip-n"
                    onClick={(e) => removeTag(t, e)}
                    aria-label={`Удалить тег ${t.tag}`}
                    title="Удалить тег у всех гостей"
                  >
                    <IconClose />
                  </button>
                </div>
              ))}
              {(data.segments || []).map((s) => (
                <div key={s.id} className="gb-chip gb-chip-segment">
                  <button
                    type="button"
                    className="gb-chip-apply"
                    onClick={() => applySegment(s)}
                    title="Применить сохранённый сегмент"
                  >
                    <IconSave /> {s.name}
                  </button>
                  <button
                    type="button"
                    className="gb-chip-n"
                    onClick={(e) => removeSegment(s.id, e)}
                    aria-label="Удалить сегмент"
                  >
                    <IconClose />
                  </button>
                </div>
              ))}
            </div>
            <input
              type="search" className="gb-search"
              placeholder="Поиск по имени, телефону или номеру карты…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <input
              type="text" className="gb-search" style={{ maxWidth: 200 }}
              placeholder="Имя сегмента…"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
            />
            <button className="ui-btn-ghost" disabled={!segmentName.trim()} onClick={saveSegment}>
              <IconSave /> Сохранить сегмент
            </button>
            <a
              className="ui-btn-ghost crm-export-btn"
              href={crmGuestsExportUrl({ status, tag, search, rfm, excluded })}
            >
              <IconDownload /> Выгрузить сегмент
            </a>
          </div>

          {checked.size > 0 && (
            <BulkBar
              count={checked.size}
              availableUsers={availableUsers}
              onTag={bulkTag}
              onCreateTask={bulkTask}
              onClear={() => setChecked(new Set())}
            />
          )}

          <div className="gb-count">
            Найдено: <strong>{formatNumber(totalFiltered)}</strong>
          </div>

          <div className="panel gb-table-panel">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={checked.size > 0 && checked.size === (data.guests || []).length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Карта</th>
                    <th>CRM</th>
                    <th title="За последние 365 дней">Траты за год</th>
                    <th title="За всё время по данным источника">Всего</th>
                    <th>Последний визит</th>
                    <th>Статус</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.guests.map((g) => (
                    <tr key={g.crm.id} className={`crm-row ${g.crm.excluded ? 'crm-row-excluded' : ''}`}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked.has(g.crm.id)}
                          onChange={() => toggle(g.crm.id)}
                        />
                      </td>
                      <td onClick={() => setSelected(g)}>
                        <div className="gb-name">{g.card || g.crm?.phone || '—'}</div>
                        {(g.crm?.name || g.name) && <div className="gb-owner">{g.crm?.name || g.name}</div>}
                      </td>
                      <td onClick={() => setSelected(g)}>
                        <div className="crm-cell-tags">
                          {(g.crm?.tags || []).map((t) => <span className="crm-mini-tag" key={t}>{t}</span>)}
                        </div>
                        <div className="crm-cell-counts">
                          {g.crm?.notes_count > 0 && <span><IconNote /> {g.crm.notes_count}</span>}
                          {g.crm?.open_tasks > 0 && <span><IconCheckCircle /> {g.crm.open_tasks}</span>}
                        </div>
                      </td>
                      <td className="gb-money" onClick={() => setSelected(g)}>
                        {g.spend_year != null ? formatMoney(g.spend_year) : '—'}
                      </td>
                      <td className="gb-money" onClick={() => setSelected(g)}>
                        {g.spend != null ? formatMoney(g.spend) : '—'}
                      </td>
                      <td onClick={() => setSelected(g)}>{g.last_visit ? formatDate(g.last_visit) : '—'}</td>
                      <td onClick={() => setSelected(g)}>
                        <span className={`gb-badge ${STATUS_META[g.status || 'unknown'].cls}`}>
                          {STATUS_META[g.status || 'unknown'].title}
                        </span>
                        {g.crm.excluded && <div className="crm-excluded-note">не учитывается</div>}
                      </td>
                      <td><button className="ui-btn-ghost" onClick={() => setSelected(g)}>Открыть</button></td>
                    </tr>
                  ))}
                  {data.guests.length === 0 && (
                    <tr><td colSpan={8} className="crm-empty">Гости не найдены</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="gb-pager">
                <button disabled={page === 0} onClick={() => setPage(page - 1)}><IconArrowLeft /> Назад</button>
                <span>{page + 1} из {pages}</span>
                <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Вперёд <IconArrowRight /></button>
              </div>
            )}
          </div>
        </>
      )}

      {selected && (
        <CrmGuestDrawer
          guest={selected}
          availableUsers={availableUsers}
          allTags={data?.tags}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </>
  );
};

const TasksTab = ({ availableUsers, currentUserId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [statusFilter, setStatusFilter] = useState('open');
  const [mineOnly, setMineOnly] = useState(false);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);

  // Канбану нужны задачи во всех стадиях сразу, поэтому status на сервер
  // больше не передаём — для списочного вида statusFilter применяется на клиенте.
  const load = useCallback(async () => {
    setLoading(true);
    const res = await getCrmTasks({
      assignee_id: mineOnly ? currentUserId : undefined,
    });
    setTasks(res.tasks || []);
    setLoading(false);
  }, [mineOnly, currentUserId]);

  useEffect(() => { load(); }, [load]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await createCrmTask({ title: title.trim(), due_date: due || null, assignee_id: assignee || null });
    setTitle(''); setDue(''); setAssignee('');
    load();
  };

  const toggle = async (t) => {
    await updateCrmTask(t.id, { status: t.status === 'done' ? 'open' : 'done' });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm('Удалить задачу?')) return;
    await deleteCrmTask(t.id);
    load();
  };

  const moveTask = async (taskId, stage) => {
    await updateCrmTask(taskId, { stage });
    load();
  };

  // Архив (разовая чистка старых авто-задач по неценным гостям — сотни штук)
  // по умолчанию скрыт и в списке, и на канбане: иначе он заслоняет живые задачи.
  const isArchived = (t) => t.status === 'done' && t.closed_reason === 'archived';
  const liveTasks = tasks.filter((t) => !isArchived(t));
  const visibleTasks = statusFilter === 'archived'
    ? tasks.filter(isArchived)
    : liveTasks.filter((t) => !statusFilter || t.status === statusFilter);

  return (
    <>
      <div className="crm-filters panel">
        <div className="gb-chips">
          <button
            className={`gb-chip ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            Список
          </button>
          <button
            className={`gb-chip ${view === 'kanban' ? 'active' : ''}`}
            onClick={() => setView('kanban')}
          >
            Канбан
          </button>
        </div>
        <div className="gb-chips">
          {view === 'list' && [['open', 'Открытые'], ['done', 'Выполненные'], ['', 'Все'], ['archived', 'Архив']].map(([key, title2]) => (
            <button
              key={key}
              className={`gb-chip ${statusFilter === key ? 'active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {title2}
            </button>
          ))}
          <button
            className={`gb-chip ${mineOnly ? 'active' : ''}`}
            onClick={() => setMineOnly((v) => !v)}
          >
            Только мои
          </button>
        </div>
      </div>

      <form className="crm-task-create panel" onSubmit={addTask}>
        <input
          type="text" placeholder="Новая задача" value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">Исполнитель</option>
          {availableUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button className="btn-primary" type="submit">Добавить</button>
      </form>

      {loading && <LoadingSpinner />}

      {!loading && view === 'kanban' && (
        <TasksKanban tasks={liveTasks} onMove={moveTask} onDelete={remove} onOpen={setSelectedTask} />
      )}

      {!loading && view === 'list' && (
        <div className="panel gb-table-panel">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Задача</th>
                  <th>Статус</th>
                  <th>Гость</th>
                  <th>Срок</th>
                  <th>Исполнитель</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleTasks.map((t) => (
                  <tr
                    key={t.id}
                    className={`crm-row ${t.status === 'done' ? 'crm-task-row-done' : ''}`}
                    onClick={() => setSelectedTask(t)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={t.status === 'done'} onChange={() => toggle(t)} />
                    </td>
                    <td>{t.title}</td>
                    <td>
                      <span className={`gb-badge tk-${t.stage || 'todo'}`}>
                        {TASK_STAGE_LABELS[t.stage] || TASK_STAGE_LABELS.todo}
                      </span>
                      {t.status === 'done' && TASK_CLOSED_REASON_LABELS[t.closed_reason] && (
                        <div className="crm-task-reason">{TASK_CLOSED_REASON_LABELS[t.closed_reason]}</div>
                      )}
                    </td>
                    <td>{t.guest_name || t.guest_card || '—'}</td>
                    <td>{t.due_date ? formatDate(t.due_date) : '—'}</td>
                    <td>{t.assignee_name}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button className="crm-task-del" onClick={() => remove(t)} aria-label="Удалить"><IconClose /></button>
                    </td>
                  </tr>
                ))}
                {visibleTasks.length === 0 && (
                  <tr><td colSpan={7} className="crm-empty">Пока нет задач</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          availableUsers={availableUsers}
          onClose={() => setSelectedTask(null)}
          onChanged={load}
        />
      )}
    </>
  );
};

const CrmPage = () => {
  const { user, isAuthenticated, organization, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('guests');
  const [guestPreset, setGuestPreset] = useState(null);

  // Из аналитики — сразу в список гостей с нужным фильтром. key у GuestsTab
  // пересоздаёт вкладку, чтобы фильтр применился, даже если она уже открыта.
  const openGuests = (preset) => {
    setGuestPreset({ ...preset, nonce: Date.now() });
    setTab('guests');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Из RFM-таблицы аналитики — сразу в форму персональных предложений с
  // выбранным сегментом: анализ → действие без ручного переноса фильтров.
  const [offerPreset, setOfferPreset] = useState(null);
  const openOffers = (preset) => {
    setOfferPreset({ ...preset, nonce: Date.now() });
    setTab('offers');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [availableUsers, setAvailableUsers] = useState([]);

  // Та же формула, что уже использует Navbar.jsx для скрытия пункта меню —
  // гейт применяется только к уже оплаченным организациям, в триале доступно всё.
  const isGated = organization?.status === 'active' && organization?.plan
    && organization.plan.has_crm === false;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && !isGated) {
      getAvailableUsers().then((res) => setAvailableUsers(res.users || [])).catch(() => {});
    }
  }, [isAuthenticated, isGated]);

  if (authLoading || !isAuthenticated) return <LoadingSpinner />;

  return (
    <div className="dashboard-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      {isGated ? (
        <PlanUpsellWall
          title="CRM"
          icon={<IconUsersGroup />}
          bullets={['База гостей и история визитов', 'Теги и сегменты', 'Задачи с назначением ответственных']}
        />
      ) : (
        <>
          <div className="dashboard-header">
            <div className="date-badge"><IconUsersGroup /> CRM</div>
            <div className="crm-tabs">
              <button className={`crm-tab ${tab === 'guests' ? 'active' : ''}`} onClick={() => { setGuestPreset(null); setTab('guests'); }}>
                Гости
              </button>
              <button className={`crm-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
                Аналитика
              </button>
              <button className={`crm-tab ${tab === 'tasks' ? 'active' : ''}`} onClick={() => setTab('tasks')}>
                Задачи
              </button>
              <button className={`crm-tab ${tab === 'promotions' ? 'active' : ''}`} onClick={() => setTab('promotions')}>
                Акции
              </button>
              <button className={`crm-tab ${tab === 'offers' ? 'active' : ''}`} onClick={() => { setOfferPreset(null); setTab('offers'); }}>
                Предложения
              </button>
              <button className={`crm-tab ${tab === 'mailings' ? 'active' : ''}`} onClick={() => setTab('mailings')}>
                Рассылки
              </button>
              <button className={`crm-tab ${tab === 'certificates' ? 'active' : ''}`} onClick={() => setTab('certificates')}>
                Сертификаты
              </button>
            </div>
          </div>

          {tab === 'guests' && (
            <GuestsTab key={guestPreset?.nonce || 'default'} availableUsers={availableUsers} preset={guestPreset} />
          )}
          {tab === 'analytics' && <AnalyticsTab onOpenGuests={openGuests} onOpenOffers={openOffers} />}
          {tab === 'tasks' && <TasksTab availableUsers={availableUsers} currentUserId={user?.id} />}
          {tab === 'promotions' && <PromotionsTab />}
          {tab === 'offers' && <OffersTab key={offerPreset?.nonce || 'default'} preset={offerPreset} />}
          {tab === 'mailings' && <MailingsTab isAdmin={user?.role === 'admin'} onOpenGuests={openGuests} />}
          {tab === 'certificates' && <CertificatesTab />}
        </>
      )}
    </div>
  );
};

export default CrmPage;
